// AB#2467 — the platform address composite's editor, as a STANDALONE component.
//
// It began life inside AddressBlockSlot, reachable only through the view renderer (component / draft /
// dataBinding). That was fine while only module entities had addresses, but Company and Site are
// hand-written pages that never touch ViewRenderer — so bringing them onto the composite meant either
// duplicating this logic or lifting it out. Lifted: AddressBlockSlot is now a thin adapter that binds
// a view field to this component, and any page can render the same editor from a plain value/onChange
// pair.
//
// Behaviour is unchanged from the slot: per-country member order and labels (addressMetadata), the
// AB#2450 lookup assist with provenance, the AB#2451 map with its Locate action, the AB#2459 country
// default, the AB#2460 "not set up" affordance and the AB#2461 field-group styling.
import type { ComponentType } from 'react'
import { AsyncLookupCombobox } from './AsyncLookupCombobox'
import { COUNTRIES } from './countries'
import { countryAddressMeta, formatAddressLines, type AddressValue } from './addressMetadata'

/** AB#2450 — the host-provided lookup seam. Absent = the editor is manual-only. */
export type FetchAddressSearch = {
    autocomplete: (query: string, countryBias?: string) => Promise<{ value: string; label: string }[]>
    resolve: (placeId: string) => Promise<Record<string, unknown> | undefined>
}

/** AB#2451 — the host-injected map. Kept out of @marque/ui so the maps lib never enters the shared chunk. */
export type AddressMapComponent = ComponentType<{
    value: Record<string, unknown>
    editable: boolean
    onGeoChange?: ((geo: { lat: number; lng: number }) => void) | undefined
    onLocate?: (() => void) | undefined
}>

export interface AddressEditorProps {
    value: AddressValue | null | undefined
    /** Called with the whole composite, or null once every member is empty. Omit for read-only. */
    onChange?: ((next: Record<string, unknown> | null) => void) | undefined
    editable?: boolean | undefined
    title?: string | undefined
    required?: boolean | undefined
    readOnly?: boolean | undefined
    showMap?: boolean | undefined
    /**
     * AB#2459 — the country a blank address opens on, so the editor does not fall back to the generic
     * international superset (which asks a UK user for a "Sorting code"). Display only: it is written
     * into the value on the first edit.
     */
    defaultCountry?: string | undefined
    fetchAddressSearch?: FetchAddressSearch | undefined
    addressMapComponent?: AddressMapComponent | undefined
}

const INPUT_CLS = 'w-full rounded border border-neutral-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-1 text-sm text-neutral-900 dark:text-slate-100 disabled:opacity-60'
const LABEL_CLS = 'text-xs font-medium text-neutral-500 dark:text-slate-400 mb-1'

// AB#2461 — lay the composite's scalar members two per row so the block stops running full-width down
// the page. The street lines keep a row to themselves (they are already a stacked pair of inputs), and a
// row a country authored with two members stays exactly as authored; only CONSECUTIVE single-member rows
// are paired, so a country's declared field order is preserved.
export function pairScalarRows(order: string[][]): string[][] {
    const out: string[][] = []
    let pending: string | null = null
    const flush = () => { if (pending) { out.push([pending]); pending = null } }
    for (const row of order) {
        if (row.length !== 1 || row[0] === 'addressLines') { flush(); out.push(row); continue }
        if (pending) { out.push([pending, row[0]!]); pending = null } else { pending = row[0]! }
    }
    flush()
    return out
}

export function AddressEditor({
    value: raw, onChange, editable, title, required, readOnly, showMap,
    defaultCountry, fetchAddressSearch, addressMapComponent,
}: AddressEditorProps) {
    const value = (raw ?? {}) as AddressValue
    const isEdit = !!editable && !!onChange
    const storedCountry = typeof value.countryCode === 'string' ? value.countryCode : ''
    const countryCode = storedCountry || (isEdit ? defaultCountry ?? '' : '')
    const meta = countryAddressMeta(countryCode || undefined)

    // AB#2450 — provenance rides ON the composite: a lookup pick stamps source LOOKUP + provider +
    // providerPlaceId; any subsequent MANUAL member edit downgrades to MANUAL and drops the place id
    // (the coordinates are kept — the address text changed, the pin may still be right, and the
    // AB#2451 draggable pin is the correction affordance).
    const emit = (next: Record<string, unknown>, opts?: { manual?: boolean }) => {
        if (!onChange) { return }
        const merged: Record<string, unknown> = { ...value, ...next }
        // AB#2459 — materialise the defaulted country on the FIRST edit, so the country the user was
        // shown is the country that gets saved. An explicit countryCode in `next` always wins.
        if (!storedCountry && countryCode && !('countryCode' in next)) { merged['countryCode'] = countryCode }
        if (opts?.manual !== false && value['source'] === 'LOOKUP' && !('source' in next)) {
            merged['source'] = 'MANUAL'
            delete merged['providerPlaceId']
        }
        for (const k of Object.keys(merged)) {
            const v = merged[k]
            const emptyArray = Array.isArray(v) && v.every(x => typeof x !== 'string' || !x.trim())
            if (v === '' || v == null || emptyArray) { delete merged[k] }
        }
        onChange(Object.keys(merged).length === 0 ? null : merged)
    }

    const pickSuggestion = (placeId: string) => {
        if (!fetchAddressSearch || !placeId) { return }
        fetchAddressSearch.resolve(placeId)
            .then(resolved => {
                if (!resolved) { return }
                emit({
                    addressLines: resolved['addressLines'],
                    dependentLocality: resolved['dependentLocality'],
                    locality: resolved['locality'],
                    administrativeArea: resolved['administrativeArea'],
                    postalCode: resolved['postalCode'],
                    sortingCode: resolved['sortingCode'],
                    countryCode: resolved['countryCode'],
                    lat: resolved['lat'],
                    lng: resolved['lng'],
                    source: 'LOOKUP',
                    provider: 'google',
                    providerPlaceId: resolved['providerPlaceId'],
                }, { manual: false })
            })
            .catch(() => { /* lookup is an assist — a failed resolve leaves manual entry untouched */ })
    }

    // AB#2451 — the Locate action: geocode the CURRENT members through the lookup (best autocomplete
    // hit → resolve), taking ONLY the coordinates — the typed address text is the user's and stays.
    const locate = () => {
        if (!fetchAddressSearch) { return }
        const query = formatAddressLines(value).join(', ')
        if (!query) { return }
        fetchAddressSearch.autocomplete(query, countryCode || undefined)
            .then(suggestions => suggestions[0] ? fetchAddressSearch.resolve(suggestions[0].value) : undefined)
            .then(resolved => {
                if (resolved && typeof resolved['lat'] === 'number' && typeof resolved['lng'] === 'number') {
                    emit({ lat: resolved['lat'], lng: resolved['lng'] }, { manual: false })
                }
            })
            .catch(() => { /* locate is an assist */ })
    }

    const AddressMap = addressMapComponent
    const map = showMap && AddressMap
        ? <div className="mt-3"><AddressMap value={value as Record<string, unknown>} editable={isEdit}
            onGeoChange={isEdit ? geo => emit({ lat: geo.lat, lng: geo.lng }, { manual: false }) : undefined}
            onLocate={isEdit && fetchAddressSearch ? locate : undefined} /></div>
        : null

    if (!isEdit) {
        const lines = formatAddressLines(value)
        const countryName = countryCode ? (COUNTRIES.find(c => c.code === countryCode)?.name ?? countryCode) : ''
        return (
            <div>
                {title && <div className={LABEL_CLS}>{title}</div>}
                {lines.length > 0 || countryName
                    ? (
                        <address className="not-italic text-sm text-neutral-900 dark:text-slate-100 space-y-0.5">
                            {lines.map((l, i) => <div key={i}>{l}</div>)}
                            {countryName && <div>{countryName}</div>}
                        </address>
                    )
                    : <div className="text-sm text-neutral-400 dark:text-slate-500 italic">No address</div>}
                {map}
            </div>
        )
    }

    const lines0 = Array.isArray(value.addressLines) ? value.addressLines.map(l => typeof l === 'string' ? l : '') : []
    const setLine = (i: number, text: string) => {
        const next = [...lines0]
        while (next.length <= i) { next.push('') }
        next[i] = text
        emit({ addressLines: next.filter((l, idx) => l.trim() || idx < 2) })
    }
    const postalCode = typeof value.postalCode === 'string' ? value.postalCode : ''
    const postalCodeInvalid = !!meta.postalCodePattern && !!postalCode
        && !new RegExp(meta.postalCodePattern).test(postalCode)
    const requiredMark = (member: string) => meta.required.includes(member)
        ? <span className="text-red-500 ml-0.5">*</span>
        : null

    return (
        <div>
            {title && (
                <div className={LABEL_CLS}>
                    {title}{required && <span className="text-red-500 ml-0.5">*</span>}
                </div>
            )}
            <div className="space-y-2">
                {/* AB#2460 — with no lookup wired the search box used to vanish silently, so nothing
                    told anyone the capability existed. The row now stays, disabled, and names what is
                    missing rather than leaving the editor looking manual-only by design. */}
                {!fetchAddressSearch && !readOnly && (
                    <div>
                        <div className={LABEL_CLS}>Find address</div>
                        <input type="text" className={INPUT_CLS} disabled placeholder="Address search is not set up" />
                        <div className="text-xs text-neutral-400 dark:text-slate-500 mt-1">
                            Add a Google Places server key under Settings → Integrations to search by postcode.
                        </div>
                    </div>
                )}
                {fetchAddressSearch && !readOnly && (
                    <div>
                        <div className={LABEL_CLS}>Find address</div>
                        {/* AB#2450 — a search box that ASSISTS: a pick fills the fields below, all of
                            which stay editable. The combobox debounces (~300ms) server-side search. */}
                        <AsyncLookupCombobox
                            value=""
                            onChange={v => pickSuggestion(String(v ?? ''))}
                            loadOptions={term => fetchAddressSearch.autocomplete(term, countryCode || undefined)}
                            placeholder="Search for an address…"
                        />
                    </div>
                )}
                <div>
                    <div className={LABEL_CLS}>Country</div>
                    <select className={INPUT_CLS} disabled={readOnly}
                        value={countryCode} onChange={e => emit({ countryCode: e.target.value })}>
                        <option value="">—</option>
                        {countryCode && !COUNTRIES.some(c => c.code === countryCode) && <option value={countryCode}>{countryCode}</option>}
                        {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
                    </select>
                </div>
                {map}
                {pairScalarRows(meta.order).map((row, ri) => (
                    <div key={ri} className={row.length > 1 ? 'grid grid-cols-2 gap-3' : undefined}>
                        {row.map(member => member === 'addressLines'
                            ? (
                                <div key={member}>
                                    <div className={LABEL_CLS}>{meta.labels[member] ?? member}{requiredMark(member)}</div>
                                    <div className="space-y-1.5">
                                        <input type="text" className={INPUT_CLS} disabled={readOnly}
                                            value={lines0[0] ?? ''} onChange={e => setLine(0, e.target.value)} />
                                        <input type="text" className={INPUT_CLS} disabled={readOnly}
                                            value={lines0[1] ?? ''} onChange={e => setLine(1, e.target.value)} />
                                    </div>
                                </div>
                            )
                            : (
                                <div key={member}>
                                    <div className={LABEL_CLS}>{meta.labels[member] ?? member}{requiredMark(member)}</div>
                                    <input type="text" className={INPUT_CLS} disabled={readOnly}
                                        value={typeof value[member] === 'string' ? String(value[member]) : ''}
                                        onChange={e => emit({ [member]: e.target.value })} />
                                    {member === 'postalCode' && postalCodeInvalid && (
                                        <div className="text-xs text-red-600 dark:text-red-400 mt-1">
                                            Not a valid {meta.labels['postalCode']?.toLowerCase() ?? 'postal code'} for the selected country.
                                        </div>
                                    )}
                                </div>
                            ))}
                    </div>
                ))}
            </div>
        </div>
    )
}
